import type { CSSProperties } from 'react';
import { Flag } from 'lucide-react';
import { isPaletteColor, LABEL_COLORS, PALETTE_HEX, type ColorValue, type LabelColor } from '@werft/domain';
import type { Swatch } from '../../ui/ColorPicker.js';
import type { LabelDto } from '@werft/contracts';
import { t, type MessageKey } from '../../i18n/index.js';

/**
 * Priorität und Labels (Paket P8). Beides erscheint nur, wenn es gesetzt ist (Regel R2). Labels entstehen beim
 * Zuweisen („Label ‚X‘ anlegen“), mit automatisch verschiedener Farbe; Umbenennen, Farbe und Löschen im Dialog
 * „Labels“ des Projektmenüs. Löschen entfernt nur die Zuordnung, keine Aufgabe.
 */

/** Feste Farbklassen (Tailwind braucht ausgeschriebene Klassen); Kontrast in hell und dunkel geprüft */
export const CHIP: Record<LabelColor, string> = {
  red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  orange: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  green: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
  pink: 'bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300',
  gray: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};
export const DOT: Record<LabelColor, string> = {
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  amber: 'bg-amber-500',
  green: 'bg-green-600',
  teal: 'bg-teal-600',
  blue: 'bg-blue-600',
  violet: 'bg-violet-600',
  pink: 'bg-pink-500',
  gray: 'bg-zinc-500',
};
export const FLAG: Record<number, string> = { 1: 'text-red-600 dark:text-red-400', 2: 'text-orange-500', 3: 'text-blue-600 dark:text-blue-400', 4: 'text-zinc-400' };

export const priorityName = (p: number) => t(`priority.${p}` as MessageKey);
/** ADR 0134: Palettenname oder „Eigene Farbe #146B62“ */
export const colorName = (c: ColorValue) => (isPaletteColor(c) ? t(`color.${c}` as MessageKey) : t('color.customNamed', { hex: c }));
/** Zentrale Auflösung statt verstreuter Farbtabellen: Chip (getönt, Schrift neutral bei eigener Farbe) und Marke */
export function chipStyle(c: ColorValue): { className: string; style?: CSSProperties } {
  return isPaletteColor(c) ? { className: CHIP[c] } : { className: 'color-chip', style: { '--c': c } as CSSProperties };
}
export function dotStyle(c: ColorValue): { className: string; style?: CSSProperties } {
  return isPaletteColor(c) ? { className: DOT[c] } : { className: '', style: { background: c } };
}
export const ColorDot = ({ color, className = 'size-2.5' }: { color: ColorValue; className?: string }) => {
  const d = dotStyle(color);
  return <span className={`shrink-0 rounded-full ${className} ${d.className}`} style={d.style} aria-hidden data-color={color} />;
};
/** Palette für den gemeinsamen Farbwähler */
export const labelSwatches = (): Swatch[] => LABEL_COLORS.map((c: LabelColor) => ({ value: c, name: colorName(c), css: PALETTE_HEX[c] }));

export function LabelChip({ label, onClick }: { label: LabelDto; onClick?: () => void }) {
  const c = chipStyle(label.color);
  const cls = `inline-flex max-w-32 items-center truncate rounded-full px-2 py-0.5 text-[11px] font-medium ${c.className}`;
  return onClick ? (
    <button type="button" tabIndex={-1} onClick={onClick} className={cls} style={c.style} title={label.name} data-label-color={label.color}>
      {label.name}
    </button>
  ) : (
    <span className={cls} style={c.style} title={label.name} data-label-color={label.color}>
      {label.name}
    </span>
  );
}

export const LabelDot = ({ color }: { color: ColorValue }) => <ColorDot color={color} />;

export function PriorityFlag({ priority, onClick }: { priority: number; onClick?: () => void }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      onClick={onClick}
      aria-label={t('priority.label', { name: priorityName(priority) })}
      className={`grid min-h-9 place-items-center rounded-lg px-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 ${FLAG[priority] ?? ''}`}
    >
      <Flag className="size-4" fill={priority <= 2 ? 'currentColor' : 'none'} aria-hidden />
    </button>
  );
}
